/**
 * StatStrip — the compact stat tiles (Story UI-U7, AC 4).
 *
 * Automatisierungsgrad (null → tile HIDDEN, no fake 0%), offene Aufgaben, Nachrichten,
 * Rechnungen in Klärung, and the P2 Placement tender counts (offene / erledigte
 * Ausschreibungen — null when the caller's visibility denies the Ausschreibungen tab →
 * tiles HIDDEN, same rule as Automatisierungsgrad). The GAPs tile is rendered ONLY when
 * the optional `gapsCount` is present on the wire (ABSENT today — the UI feature-detects,
 * never stubs). Neutral chrome only — color = signal, these are plain figures.
 *
 * [Source: docs/stories/ui-u7-portfolio.md AC 4]
 */

import type { PortfolioStatsDto } from '@brokly/shared';
import { Card } from '../../../components/ui/Card';

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <Card className="rounded-xl p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mono-num mt-1 text-lg font-semibold text-foreground">{value}</div>
    </Card>
  );
}

export function StatStrip({
  stats,
  gapsCount,
}: {
  stats: PortfolioStatsDto;
  gapsCount?: number;
}) {
  const showAuto = stats.automatisierungsgrad_pct !== null;
  const showGaps = gapsCount !== undefined;
  // P2 Placement: both tender counts are nulled together when the Ausschreibungen tab is denied.
  const showAusschreibungen =
    stats.ausschreibungen_offen !== null && stats.ausschreibungen_erledigt !== null;

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
      {showAuto && (
        <StatTile label="Automatisierungsgrad" value={`${stats.automatisierungsgrad_pct}%`} />
      )}
      <StatTile label="Offene Aufgaben" value={String(stats.offene_aufgaben)} />
      <StatTile label="Nachrichten" value={String(stats.offene_nachrichten)} />
      <StatTile label="Rechnungen in Klärung" value={String(stats.rechnungen_in_klaerung)} />
      {showAusschreibungen && (
        <>
          <StatTile label="Offene Ausschreibungen" value={String(stats.ausschreibungen_offen)} />
          <StatTile label="Erledigte Ausschreibungen" value={String(stats.ausschreibungen_erledigt)} />
        </>
      )}
      {showGaps && <StatTile label="GAPs" value={String(gapsCount)} />}
    </div>
  );
}
